import React, { createContext, Dispatch, SetStateAction, useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Card, Divider, Result, Spin, StepProps, Steps, Typography } from "antd"

import { useProcurarViagemPelaSenha } from "@/Services/Domain/Viagem/ViagemService";
import { TypeInscricaoDoAlunoNaViagem } from "@/Models/Domain/Viagem/InscricaoDoAlunoNaViagem";

import { DadosBasicos } from "./Passos/DadosBasicos";
import { PagamentoDaViagem } from "./Passos/PagamentoDaViagem";
import { FichaDeSaude as FichaDeSaudeStep } from "./Passos/FichaDeSaude";
import { AutorizacaoDaViagem } from "./Passos/AutorizacaoDaViagem";
import { Minuta } from "./Passos/Minuta";
import { useObterInscricaoViagemAluno, useSalvarUltimoPassoInscricao } from "@/Services/Domain/Viagem/InscricaoService";

type PassoType = StepProps & { content : React.ReactNode, index : number; };

export const ContextInscricao = createContext<{
    inscricao:TypeInscricaoDoAlunoNaViagem;
    setInscricao:Dispatch<SetStateAction<TypeInscricaoDoAlunoNaViagem>>
}>({ } as any);

export const InscricaoDaViagem: React.FC<{
    senha?: string,
    fallback?: string
}> = ({ ...props}) => {
    const fallbackRoute = props.fallback ?? "/";

    const navigate = useNavigate();
    const [ params ] = useSearchParams();

    const [ inscricao, setInscricao ] = useState({} as TypeInscricaoDoAlunoNaViagem);
    const [ passoAtual, setPassoAtual ] = useState<number>(0);

    const {
        mutate : mutationBuscarViagem,
        isPending : isPedingViagem,
        isSuccess : isSucessViagem,
        isError : isErrorViagem
    } =  useProcurarViagemPelaSenha({
        onSuccess: (res) => {
            const data = res.data;
            setInscricao(prev => {                
                const codigoAluno = Number.parseInt(params.get("a") ?? "0");

                prev.viagem = data?.viagem;
                prev.codViagem = data?.viagem?.codigo;
                prev.aluno = {
                    ...prev.aluno,
                    codigo : codigoAluno
                }

                return prev;
            });
        },
        onError: (_) => {
            navigate(fallbackRoute);
        }
    });
    
    const {
        isPending : isPedingInscricaoViagemAluno,
        isSuccess : isSucessInscricaoViagemAluno,
        isError : isErrorInscricaoViagemAluno
    } = useObterInscricaoViagemAluno({
        codViagem : inscricao.viagem?.codigo ?? 0,
        codAluno : inscricao.aluno?.codigo ?? 0,
        enabled : (inscricao.viagem?.codigo ?? 0) > 0 && (inscricao.aluno?.codigo ?? 0) > 0,
        onSuccess(data) {
            setInscricao((prev) => {
                const novo = {
                    ...(prev ?? {}),
                    ...data,
                    aluno : data?.aluno ?? {},
                    viagem : {
                        ...(prev.viagem ?? {}),
                        codigo : data?.codViagem ?? (prev.viagem?.codigo ?? 0)
                    }
                } as TypeInscricaoDoAlunoNaViagem;
                
                irParaPasso((novo?.passo ?? 0) === 0 ? 1 : novo.passo, novo);

                return novo;
            });
        }
    });

    const {
        mutate : mutationSalvarUltimoPasso,
        isPending : isSalvarUltimoPassoViagem
    } = useSalvarUltimoPassoInscricao({
        
    });

    const irParaPasso = (passoParam?: number, inscricaoParam?:TypeInscricaoDoAlunoNaViagem) => {        
        const passo = passoParam ?? passoAtual;

        mutationSalvarUltimoPasso({
            codViagem : inscricaoParam?.codViagem ?? inscricao.viagem?.codigo,
            codAluno : inscricaoParam?.codAluno ??  inscricao.aluno?.codigo,
            passo : passo
        }, {
            onSuccess() {
                const novoPasso = passosHabilitados.find(f => f.index >= passo)?.index ?? -1;
                setPassoAtual(novoPasso === -1 ? (passosHabilitados.reverse().find(f => true)?.index ?? 0) : novoPasso);
            }
        });
    }

    const passoAnterior = () => {
        const novoPasso = passosHabilitados.reverse().find(f => f.index < passoAtual)?.index ?? -1;
        if (novoPasso === -1) navigate(fallbackRoute);
        setPassoAtual(novoPasso);
    }

    useEffect(() => {
        const senhaComoParametro = params.get("s");
        const senha = props.senha ?? senhaComoParametro;

        if (senha == undefined) {
            navigate(fallbackRoute);
            return;
        };

        mutationBuscarViagem({ senha : senha });
    }, []);

    const passos : PassoType[] = [
        {
            index : 0,
            title: "Dados básicos",
            disabled : false,
            content: (
                <DadosBasicos
                    textoSalvar="Salvar e avançar"
                    textoCancelar="Sair"
                    onSucesso={(data) => {
                        irParaPasso(1, data);
                    }}
                    onCancelar={() => {
                        passoAnterior();
                    }}
                />
            ),
        },
        {
            index : 1,
            title: "Ficha de saúde",
            disabled : !(inscricao.viagem?.requerFichaDeSaude ?? false),
            content: (
                <React.Fragment>
                    {inscricao.aluno?.codigo ? (
                        <FichaDeSaudeStep txtProximo="Salvar e avançar" txtCancelar="Voltar" codigoDoAluno={inscricao.aluno.codigo} onCancelar={() => passoAnterior()} onSucesso={() => irParaPasso(2)} />
                    ) : (
                        <Result status={"500"} title={"Ocorreu um erro inesperado"} />
                    )}
                </React.Fragment>
            ),
        },
        {
            index : 2,
            title : "Autorização de viagem",
            disabled : !(inscricao.viagem?.requerAutorizacaoParaMenores ?? false),
            content : <AutorizacaoDaViagem
                textoSalvar="Salvar e avançar"
                textoCancelar="Voltar"
                onSucesso={() => {
                    irParaPasso(3);
                }}
                onCancelar={() => {
                    passoAnterior();
                }}
            />,
        },
        {
            index : 3,
            title : "Contrato de viagem",
            disabled : !inscricao.viagem?.minuta,
            content : <Minuta
                textoSalvar="Salvar e avançar"
                textoCancelar="Voltar"
                onSucesso={() => {
                    irParaPasso(4);
                }}
                onCancelar={() => {
                    passoAnterior();
                }}
            />,
        },
        {
            index : 4,
            title: "Pagamento",
            disabled : (inscricao.viagem?.pago ?? true),
            content: (
                <PagamentoDaViagem
                    inscricaoAtual={inscricao}
                    viagem={inscricao.viagem!}
                    onCancelar={() => {
                        passoAnterior();
                    }}
                    textoCancelar={"Voltar"}
                />
            ),
        },
    ];

    const passosHabilitados = passos.filter(f => f.disabled === undefined || f.disabled === false);

    if ((isSucessViagem || isErrorViagem) && !isPedingViagem && (inscricao.viagem ?? null) === null) return (
        <Result
            status="404"
            title="Não encontrado"
            subTitle="Viagem não encontrada!"
            extra={
                <Button type="primary" onClick={() => navigate(fallbackRoute)}>
                    Voltar
                </Button>
            }
        />
    );

    return <ContextInscricao.Provider value={{
        inscricao, 
        setInscricao
    }}>
        <Spin spinning={(inscricao.viagem ?? null) === null || isPedingViagem || (isPedingInscricaoViagemAluno && (isSucessInscricaoViagemAluno || isErrorInscricaoViagemAluno)) || isSalvarUltimoPassoViagem}>
            <Card style={{ minHeight: "100%" }}>
                <Typography.Title style={{margin: 0}} level={3}>
                    Inscrição para a viagem {inscricao.viagem?.titulo}
                </Typography.Title>

                <Divider />

                <Steps 
                    items={passosHabilitados as StepProps[]} 
                    current={passosHabilitados.findIndex(f => f.index === passoAtual) ?? 0}
                />

                <Divider />

                {passos[passoAtual]?.content}

                {process.env.NODE_ENV === "development" && <>
                    <Divider />
                    <div>Passo atual: {passoAtual}</div>
                    <div>Inscrição: </div>
                    <div>
                        <pre>
                            {JSON.stringify(inscricao, null, 4)}
                        </pre>
                    </div>
                </>} 
            </Card>
        </Spin>
    </ContextInscricao.Provider>;
}
